import { useState, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { Menu, X, ChevronDown } from "lucide-react";
import { COMPANY } from "@/lib/seo";
import hadleyLogo from "@/assets/hadley-logo.webp";
import { Button } from "@/components/ui/button";

const navItems = [
  {
    label: "Electrical", href: "/electrical-services", children: [
      { label: "Electrical Services Overview", href: "/electrical-services" },
      { label: "Service & Repair", href: "/electrical-services/electrical-service-and-repair" },
      { label: "Electrical Contracting", href: "/electrical-services/commercial-electrical-contractor" },
      { label: "Preventative Maintenance", href: "/electrical-services/preventative-maintenance" },
      { label: "Panel Upgrades & Switchgear", href: "/electrical-services/electrical-panel-upgrades" },
      { label: "Generator Installation", href: "/electrical-services/generator-installation" },
      { label: "Motor Control Centers", href: "/electrical-services/motor-control-centers" },
      { label: "PLC & Motor Controls", href: "/electrical-services/plc-motor-controls" },
      { label: "Fire Alarm Systems", href: "/electrical-services/fire-alarm-systems" },
      { label: "Data Cabling & Low Voltage", href: "/electrical-services/data-cabling-low-voltage" },
      { label: "Electrical Design-Build", href: "/electrical-services/electrical-design-build" },
      { label: "Safety Inspections", href: "/electrical-services/electrical-safety-inspections" },
      { label: "24/7 Emergency Service", href: "/electrical-services/emergency-electrical-service" },
    ]
  },
  {
    label: "HVAC", href: "/hvac-services", children: [
      { label: "HVAC Services Overview", href: "/hvac-services" },
      { label: "Commercial HVAC", href: "/hvac-services/commercial-hvac" },
      { label: "Industrial HVAC Systems", href: "/hvac-services/industrial-hvac" },
      { label: "Residential HVAC", href: "/hvac-services/residential-hvac" },
      { label: "HVAC Installation", href: "/hvac-services/hvac-installation" },
      { label: "HVAC Repair & Service", href: "/hvac-services/hvac-service-repair" },
      { label: "HVAC Preventative Maintenance", href: "/hvac-services/hvac-preventative-maintenance" },
      { label: "Building Automation (BAS)", href: "/hvac-services/building-automation-systems" },
      { label: "Temperature Controls", href: "/hvac-services/temperature-controls" },
      { label: "Ductwork Installation", href: "/hvac-services/ductwork-installation" },
      { label: "Heat Pump Installation", href: "/hvac-services/heat-pump-installation" },
      { label: "Furnace Installation & Repair", href: "/hvac-services/furnace-installation-repair" },
      { label: "AC Installation & Repair", href: "/hvac-services/ac-installation-repair" },
      { label: "Geothermal Heating & Cooling", href: "/hvac-services/geothermal-heating-cooling" },
      { label: "Indoor Air Quality", href: "/hvac-services/indoor-air-quality" },
    ]
  },
  {
    label: "Energy & Lighting", href: "/energy-management", children: [
      { label: "Energy Management Overview", href: "/energy-management" },
      { label: "Lighting Design", href: "/energy-management/lighting-design" },
      { label: "Lighting Retrofits & LED", href: "/energy-management/lighting-retrofits" },
      { label: "Energy Audits", href: "/energy-management/energy-audits" },
      { label: "Energy Bill Analysis", href: "/energy-management/energy-bill-analysis" },
      { label: "Guaranteed Energy Savings", href: "/energy-management/guaranteed-energy-savings" },
      { label: "Energy Efficiency Consulting", href: "/energy-management/energy-efficiency-consulting" },
      { label: "Stadium Lighting Overview", href: "/stadium-sports-lighting" },
      { label: "Football & Baseball Fields", href: "/stadium-sports-lighting/sports-field-lighting" },
      { label: "Arena & Gymnasium Lighting", href: "/stadium-sports-lighting/arena-event-lighting" },
      { label: "LED Sports Lighting Retrofit", href: "/stadium-sports-lighting/led-stadium-retrofits" },
      { label: "High School Athletic Lighting", href: "/stadium-sports-lighting/outdoor-sports-lighting" },
    ]
  },
  {
    label: "Industries", href: "/industries", children: [
      { label: "Industries Overview", href: "/industries" },
      { label: "Manufacturing & Automotive", href: "/industries/manufacturing-electrical" },
      { label: "Warehouse & Distribution", href: "/industries/warehouse-electrical" },
      { label: "Healthcare & Hospitals", href: "/industries/healthcare-facility-compliance" },
      { label: "Schools & Education", href: "/industries/school-electrical-contractor" },
      { label: "Hotels & Hospitality", href: "/industries/hotel-electrical-contractor" },
      { label: "Churches & Religious", href: "/industries/church-electrical-contractor" },
      { label: "Government & Municipal", href: "/industries/municipal-electrical" },
      { label: "Restaurants & Retail", href: "/industries/restaurant-electrical" },
    ]
  },
  {
    label: "About", href: "/about", children: [
      { label: "About Us", href: "/about" },
      { label: "Portfolio", href: "/portfolio" },
      { label: "Reviews", href: "/reviews" },
      { label: "Blog", href: "/blog" },
      { label: "Careers", href: "/about/careers" },
      { label: "Safety", href: "/about/safety" },
      { label: "FAQ", href: "/about/faq" },
    ]
  },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const location = useLocation();

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  

  return (
    <>
      <div className="hidden h-8 bg-charcoal text-charcoal-foreground md:block">
        <div className="container flex h-full items-center justify-between font-condensed text-xs font-bold uppercase tracking-[0.08em]">
          <span className="text-charcoal-foreground/70">Licensed KY Contractor CE64623 · ME64500 · HM06538</span>
          <span><span className="text-signal">24/7 Emergency</span><span className="mx-2 text-charcoal-foreground/25">·</span><a href={`tel:${COMPANY.phoneTel}`} data-cta="header-topbar" className="text-charcoal-foreground hover:text-primary-light">{COMPANY.phone}</a></span>
        </div>
      </div>

      <header data-testid="sticky-header" className="sticky top-0 z-50 h-[76px] border-b border-border bg-card">
        <div className="container flex h-full items-center justify-between gap-4">
          <Link to="/" className="flex h-full w-[150px] shrink-0 items-center" aria-label="Hadley Energy Solutions home">
            <img src={hadleyLogo} alt="Hadley Energy Solutions - Electrical, HVAC & Energy" className="h-[68px] w-[150px] object-contain" width="1428" height="883" />
          </Link>

          <nav className="hidden h-full flex-1 items-stretch justify-center lg:flex" aria-label="Primary navigation">
            {navItems.map(item => (
              <div key={item.label} className="relative flex" onMouseEnter={() => setDropdownOpen(item.label)} onMouseLeave={() => setDropdownOpen(null)} onFocus={() => setDropdownOpen(item.label)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDropdownOpen(null); }}>
                <Button variant="ghost" className={`h-full rounded-none px-3 font-heading text-[14px] font-bold ${location.pathname.startsWith(item.href) ? "text-primary" : "text-foreground"}`} aria-expanded={dropdownOpen === item.label}>
                  {item.label}<ChevronDown className="h-3.5 w-3.5" />
                </Button>
                {dropdownOpen === item.label && (
                  <div className="absolute left-1/2 top-full z-50 w-[520px] -translate-x-1/2 border border-border bg-card p-3 shadow-lg">
                    <div className="grid grid-cols-2">
                      {item.children.map(child => <Link key={child.href + child.label} to={child.href} onClick={() => setDropdownOpen(null)} className="px-3 py-2 font-body text-sm font-medium text-foreground hover:bg-secondary hover:text-primary">{child.label}</Link>)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden shrink-0 items-center gap-4 lg:flex">
            <a href={`tel:${COMPANY.phoneTel}`} data-cta="header" className="whitespace-nowrap font-heading text-sm font-bold text-foreground hover:text-primary">{COMPANY.phone}</a>
            <Button asChild className="h-11 rounded px-5 font-heading font-bold"><Link to="/request-estimate">Request Estimate</Link></Button>
          </div>

          <Button variant="ghost" size="icon" className="shrink-0 lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {mobileOpen && (
          <div className="max-h-[calc(100vh-76px)] overflow-y-auto border-t border-border bg-card lg:hidden">
            <nav className="container space-y-1 py-4" aria-label="Mobile navigation">
              {navItems.map(item => (
                <div key={item.label}>
                  <Button variant="ghost" onClick={() => setMobileSubmenu(mobileSubmenu === item.label ? null : item.label)} className="h-11 w-full justify-between rounded-none px-3 font-heading text-base font-bold">
                    {item.label}<ChevronDown className={`h-4 w-4 transition-transform ${mobileSubmenu === item.label ? "rotate-180" : ""}`} />
                  </Button>
                  {mobileSubmenu === item.label && <div className="grid grid-cols-1 border-l border-border pb-2 pl-3">{item.children.map(child => <Link key={child.href + child.label} to={child.href} onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm font-medium hover:text-primary">{child.label}</Link>)}</div>}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-4">
                <Button asChild variant="outline"><a href={`tel:${COMPANY.phoneTel}`} data-cta="mobile-menu">{COMPANY.phone}</a></Button>
                <Button asChild><Link to="/request-estimate" onClick={() => setMobileOpen(false)}>Request Estimate</Link></Button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
